// Invoice detail — the line-item editor. Shows the invoice header (client,
// status, dates), an inline line-item table where each row's line_total and the
// invoice subtotal/total are server-computed (the client never writes them),
// and the status workflow actions (send / mark-paid / void).
//
// Line items are added/edited/deleted inline; after each mutation we refetch
// the invoice so the DB-generated line_total and the running subtotal/total
// stay authoritative. Editing the invoice subtotal/tax is a separate metadata
// form (subtotal is kept in sync with the line items by a "Recalculate" action).
//
// NOTE: route for this page is wired in App.tsx during the Integration pass at
// path /app/invoices/:id.

import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  Invoice,
  InvoiceLineItem,
  InvoiceStatus,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Table,
  Textarea,
} from '../components/ui';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { ApiError } from '../api/client';
import {
  useInvoice,
  useUpdateInvoice,
  useSendInvoice,
  useMarkInvoicePaid,
  useVoidInvoice,
  useCreateLineItem,
  useUpdateLineItem,
  useDeleteLineItem,
} from '../api/queries/billing';
import { invoiceStatusTone } from './InvoicesPage';

// ---------- Helpers ----------

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
};

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number | null): string {
  return epochMs === null ? '—' : new Date(epochMs).toLocaleDateString();
}

function fmtMoney(value: string | number): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

const MONEY_RE = /^\d+(\.\d{1,2})?$/;

// =============================================================================
// Page
// =============================================================================

export function InvoiceDetailPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const validId = Number.isInteger(id) && id > 0;

  const canWrite = can('invoice.write');
  const canSend = can('invoice.send');
  const canMarkPaid = can('invoice.mark_paid');
  const canVoid = can('invoice.void');

  const invoiceQuery = useInvoice(validId ? id : null);
  const send = useSendInvoice();
  const markPaid = useMarkInvoicePaid();
  const voidInvoice = useVoidInvoice();

  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState<'send' | 'mark-paid' | 'void' | null>(null);

  if (!validId) {
    return <p className="error-text">Invalid invoice id.</p>;
  }
  if (invoiceQuery.isLoading) {
    return <p className="muted">Loading invoice…</p>;
  }
  if (invoiceQuery.isError || !invoiceQuery.data) {
    return (
      <p className="error-text" role="alert">
        {errMessage(invoiceQuery.error, 'Failed to load invoice.')}
      </p>
    );
  }

  const invoice = invoiceQuery.data.invoice;
  const lineItems = invoice.line_items;
  const status = invoice.status;

  const canBeSent = status === 'draft' || status === 'overdue';
  const canBePaid = status === 'sent' || status === 'overdue';
  const canBeVoided = status !== 'paid' && status !== 'void';
  const editable = canWrite && status !== 'void' && status !== 'paid';

  const onConfirmAction = async (): Promise<void> => {
    try {
      if (confirm === 'send') {
        await send.mutateAsync(invoice.id);
        toast.success('Invoice sent.');
      } else if (confirm === 'mark-paid') {
        await markPaid.mutateAsync(invoice.id);
        toast.success('Invoice marked paid.');
      } else if (confirm === 'void') {
        await voidInvoice.mutateAsync(invoice.id);
        toast.success('Invoice voided.');
      }
      setConfirm(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Action failed.'));
    }
  };

  const actionBusy = send.isPending || markPaid.isPending || voidInvoice.isPending;

  return (
    <div className="invoice-detail-page">
      <header className="page-header">
        <div>
          <Link to="/app/invoices" className="link back-link">
            ← Invoices
          </Link>
          <h2>
            Invoice {invoice.invoice_no}{' '}
            <span className={`badge ${invoiceStatusTone(status)}`}>
              {STATUS_LABELS[status]}
            </span>
          </h2>
        </div>
        <div className="detail-actions">
          {editable && (
            <Button variant="ghost" onClick={() => setEditOpen(true)}>
              Edit details
            </Button>
          )}
          {canSend && canBeSent && (
            <Button variant="primary" onClick={() => setConfirm('send')}>
              Send
            </Button>
          )}
          {canMarkPaid && canBePaid && (
            // No primitive variant maps to `.btn-success`; keep the native
            // button so the existing color is preserved exactly.
            <button
              type="button"
              className="btn btn-success"
              onClick={() => setConfirm('mark-paid')}
            >
              Mark paid
            </button>
          )}
          {canVoid && canBeVoided && (
            <Button variant="danger" onClick={() => setConfirm('void')}>
              Void
            </Button>
          )}
        </div>
      </header>

      {/* Header facts */}
      <dl className="detail-facts">
        <div>
          <dt>Issue date</dt>
          <dd>{fmtDate(invoice.issue_date)}</dd>
        </div>
        <div>
          <dt>Due date</dt>
          <dd>{fmtDate(invoice.due_date)}</dd>
        </div>
        <div>
          <dt>Contract</dt>
          <dd>{invoice.contract_id !== null ? `#${invoice.contract_id}` : '—'}</dd>
        </div>
        <div>
          <dt>Sent</dt>
          <dd>{fmtDate(invoice.sent_at)}</dd>
        </div>
        <div>
          <dt>Paid</dt>
          <dd>{fmtDate(invoice.paid_at)}</dd>
        </div>
      </dl>

      {invoice.notes && <p className="detail-description">{invoice.notes}</p>}

      {/* Line items */}
      <LineItemsEditor
        invoiceId={invoice.id}
        lineItems={lineItems}
        editable={editable}
      />

      {/* Totals — all server-computed */}
      <div className="invoice-totals">
        <div className="totals-row">
          <span>Subtotal</span>
          <span>{fmtMoney(invoice.subtotal)}</span>
        </div>
        <div className="totals-row">
          <span>Tax</span>
          <span>{fmtMoney(invoice.tax_amount)}</span>
        </div>
        <div className="totals-row totals-grand">
          <strong>Total</strong>
          <strong>{fmtMoney(invoice.total)}</strong>
        </div>
        <p className="muted form-hint">
          Each line total and the invoice total are computed by the system. Use
          “Edit details” to adjust the subtotal/tax used for the grand total.
        </p>
      </div>

      {editOpen && (
        <EditInvoiceModal
          invoice={invoice}
          lineItems={lineItems}
          onClose={() => setEditOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={
          confirm === 'send'
            ? 'Send invoice'
            : confirm === 'mark-paid'
              ? 'Mark invoice paid'
              : 'Void invoice'
        }
        message={
          confirm === 'send'
            ? `Send invoice ${invoice.invoice_no} to the client?`
            : confirm === 'mark-paid'
              ? `Mark invoice ${invoice.invoice_no} as paid?`
              : `Void invoice ${invoice.invoice_no}? This cannot be undone.`
        }
        confirmLabel={
          confirm === 'send' ? 'Send' : confirm === 'mark-paid' ? 'Mark paid' : 'Void'
        }
        danger={confirm === 'void'}
        busy={actionBusy}
        onConfirm={onConfirmAction}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

// =============================================================================
// Line item editor
// =============================================================================

interface LineItemsEditorProps {
  invoiceId: number;
  lineItems: InvoiceLineItem[];
  editable: boolean;
}

function LineItemsEditor({
  invoiceId,
  lineItems,
  editable,
}: LineItemsEditorProps): JSX.Element {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<InvoiceLineItem | null>(null);
  const [deleting, setDeleting] = useState<InvoiceLineItem | null>(null);

  const deleteLine = useDeleteLineItem();

  const linesSubtotal = useMemo(
    () => lineItems.reduce((sum, li) => sum + Number(li.line_total || '0'), 0),
    [lineItems],
  );

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteLine.mutateAsync({ invoiceId, lineId: deleting.id });
      toast.success('Line item removed.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to remove line item.'));
    }
  };

  return (
    <section className="page-section">
      <div className="section-head">
        <h3>Line items</h3>
        {editable && (
          // No primitive variant maps to `.btn-secondary`; keep the native
          // button so the existing color is preserved exactly.
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setAdding(true)}
          >
            Add line item
          </button>
        )}
      </div>

      {lineItems.length === 0 ? (
        <p className="muted empty-state">No line items yet.</p>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Description</Table.HeaderCell>
              <Table.HeaderCell>Source</Table.HeaderCell>
              <Table.HeaderCell>Qty</Table.HeaderCell>
              <Table.HeaderCell>Unit</Table.HeaderCell>
              <Table.HeaderCell>Line total</Table.HeaderCell>
              {editable && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {lineItems.map((li) => (
              <Table.Row key={li.id}>
                <Table.Cell tone="strong">{li.description ?? '—'}</Table.Cell>
                <Table.Cell>
                  <span className="badge badge-muted">{li.source_type}</span>
                </Table.Cell>
                <Table.Cell>{li.quantity}</Table.Cell>
                <Table.Cell>{fmtMoney(li.unit_amount)}</Table.Cell>
                <Table.Cell>{fmtMoney(li.line_total)}</Table.Cell>
                {editable && (
                  <Table.Cell className="col-actions">
                    <Button
                      variant="ghost"
                      className="btn-sm"
                      onClick={() => setEditing(li)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => setDeleting(li)}
                    >
                      Delete
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
          <tfoot>
            <tr>
              <td colSpan={editable ? 4 : 4} className="totals-label">
                Line items total
              </td>
              <td>{fmtMoney(linesSubtotal)}</td>
              {editable && <td />}
            </tr>
          </tfoot>
        </Table>
      )}

      {adding && (
        <LineItemFormModal
          invoiceId={invoiceId}
          onClose={() => setAdding(false)}
        />
      )}

      {editing && (
        <LineItemFormModal
          invoiceId={invoiceId}
          lineItem={editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Remove line item"
        message="Remove this line item from the invoice?"
        confirmLabel="Remove"
        danger
        busy={deleteLine.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </section>
  );
}

// =============================================================================
// Line item create/edit modal
// =============================================================================

interface LineItemFormModalProps {
  invoiceId: number;
  lineItem?: InvoiceLineItem;
  onClose: () => void;
}

function LineItemFormModal({
  invoiceId,
  lineItem,
  onClose,
}: LineItemFormModalProps): JSX.Element {
  const isEdit = lineItem !== undefined;
  const create = useCreateLineItem();
  const update = useUpdateLineItem();
  const busy = create.isPending || update.isPending;

  const [description, setDescription] = useState(lineItem?.description ?? '');
  const [quantity, setQuantity] = useState(lineItem?.quantity ?? '1');
  const [unitAmount, setUnitAmount] = useState(lineItem?.unit_amount ?? '0');

  const preview = useMemo(() => {
    const q = Number(quantity);
    const u = Number(unitAmount);
    if (!Number.isFinite(q) || !Number.isFinite(u)) return null;
    return Math.round(q * u * 100) / 100;
  }, [quantity, unitAmount]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const q = quantity.trim();
    const u = unitAmount.trim();
    if (!MONEY_RE.test(q)) {
      toast.warn('Quantity must be a non-negative number.');
      return;
    }
    if (!MONEY_RE.test(u)) {
      toast.warn('Unit amount must be a non-negative number.');
      return;
    }
    const desc = description.trim();

    try {
      if (isEdit && lineItem) {
        await update.mutateAsync({
          invoiceId,
          lineId: lineItem.id,
          description: desc ? desc : null,
          quantity: q,
          unit_amount: u,
        });
        toast.success('Line item updated.');
      } else {
        await create.mutateAsync({
          invoiceId,
          source_type: 'manual',
          quantity: q,
          unit_amount: u,
          ...(desc ? { description: desc } : {}),
        });
        toast.success('Line item added.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save line item.'));
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Edit line item' : 'Add line item'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="line-item-form"
            variant="primary"
            disabled={busy}
          >
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <form id="line-item-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Description">
          <Input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            placeholder="Service description"
            autoFocus
          />
        </FormField>

        <FormRow>
          <FormField label="Quantity">
            <Input
              type="text"
              inputMode="decimal"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Unit amount (USD)">
            <Input
              type="text"
              inputMode="decimal"
              value={unitAmount}
              onChange={(e) => setUnitAmount(e.target.value)}
              required
            />
          </FormField>
        </FormRow>

        <div className="totals-summary">
          <span>Line total (computed):</span>{' '}
          <strong>{preview === null ? '—' : fmtMoney(preview)}</strong>
        </div>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Edit invoice metadata modal
// =============================================================================

interface EditInvoiceModalProps {
  invoice: Invoice;
  lineItems: InvoiceLineItem[];
  onClose: () => void;
}

function EditInvoiceModal({
  invoice,
  lineItems,
  onClose,
}: EditInvoiceModalProps): JSX.Element {
  const update = useUpdateInvoice();
  const busy = update.isPending;

  const [invoiceNo, setInvoiceNo] = useState(invoice.invoice_no);
  const [issueDate, setIssueDate] = useState<number | null>(invoice.issue_date);
  const [dueDate, setDueDate] = useState<number | null>(invoice.due_date);
  const [subtotal, setSubtotal] = useState(invoice.subtotal);
  const [taxAmount, setTaxAmount] = useState(invoice.tax_amount);
  const [notes, setNotes] = useState(invoice.notes ?? '');

  // Sum of current line totals — offered as a one-click way to bring the
  // stored subtotal in line with the line items.
  const linesSubtotal = useMemo(
    () =>
      lineItems
        .reduce((sum, li) => sum + Number(li.line_total || '0'), 0)
        .toFixed(2),
    [lineItems],
  );

  const grand = useMemo(() => {
    const s = Number(subtotal);
    const t = Number(taxAmount);
    if (!Number.isFinite(s) || !Number.isFinite(t)) return null;
    return s + t;
  }, [subtotal, taxAmount]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmedNo = invoiceNo.trim();
    if (!trimmedNo) {
      toast.warn('Invoice number is required.');
      return;
    }
    const s = subtotal.trim();
    const t = taxAmount.trim();
    if (!MONEY_RE.test(s)) {
      toast.warn('Subtotal must be a non-negative amount.');
      return;
    }
    if (!MONEY_RE.test(t)) {
      toast.warn('Tax must be a non-negative amount.');
      return;
    }
    const trimmedNotes = notes.trim();

    try {
      await update.mutateAsync({
        id: invoice.id,
        invoice_no: trimmedNo,
        subtotal: s,
        tax_amount: t,
        issue_date: issueDate,
        due_date: dueDate,
        notes: trimmedNotes ? trimmedNotes : null,
      });
      toast.success('Invoice updated.');
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to update invoice.'));
    }
  };

  return (
    <Dialog
      title={`Edit ${invoice.invoice_no}`}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-invoice-form"
            variant="primary"
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="edit-invoice-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Invoice number">
          <Input
            type="text"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
            maxLength={120}
            required
          />
        </FormField>

        <FormRow>
          <FormField label="Issue date">
            <DateInput value={issueDate} onChange={setIssueDate} />
          </FormField>
          <FormField label="Due date">
            <DateInput value={dueDate} onChange={setDueDate} />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Subtotal (USD)">
            <Input
              type="text"
              inputMode="decimal"
              value={subtotal}
              onChange={(e) => setSubtotal(e.target.value)}
              required
            />
            <Button
              variant="ghost"
              className="btn-sm"
              onClick={() => setSubtotal(linesSubtotal)}
            >
              Set to line items total ({fmtMoney(linesSubtotal)})
            </Button>
          </FormField>
          <FormField label="Tax (USD)">
            <Input
              type="text"
              inputMode="decimal"
              value={taxAmount}
              onChange={(e) => setTaxAmount(e.target.value)}
              required
            />
          </FormField>
        </FormRow>

        <div className="totals-summary">
          <strong>Total: {grand === null ? '—' : fmtMoney(grand)}</strong>
        </div>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={20_000}
            placeholder="Optional"
          />
        </FormField>
      </form>
    </Dialog>
  );
}
